import { useWide } from '../shell/hooks';

// Steps through a flow (Import a list; consistency pass R9 M6, Kristi Nov 2). On a laptop: the
// numbered steps in a row, ✓ on the ones done, a step you can go back to is a button. On a phone
// the row would wrap, so it's one line, "Step 3 of 4 · Check the matches", a thin progress bar
// under it and "Next: Where they go".

export interface Step<K extends string> {
  key: K;
  name: string;
}

export function Stepper<K extends string>({
  steps,
  current,
  onPick,
  canGo,
  label = 'Steps',
  narrow: narrowProp,
}: {
  steps: Step<K>[];
  current: K;
  /** Go to a step (only steps `canGo` allows). */
  onPick?: (k: K) => void;
  /** Whether a step can be gone to now; defaults to the ones already done. */
  canGo?: (k: K) => boolean;
  label?: string;
  /** The phone layout; defaults to the app's (under 700 px). */
  narrow?: boolean;
}) {
  const wide = useWide();
  const narrow = narrowProp ?? !wide;
  const at = Math.max(0, steps.findIndex((s) => s.key === current));
  const reach = (k: K, i: number) => (canGo ? canGo(k) : i < at);

  if (narrow) {
    const next = steps[at + 1];
    return (
      <div class="stepper narrow" role="group" aria-label={label}>
        <div class="stepper-now" aria-current="step">
          Step {at + 1} of {steps.length} · {steps[at]?.name}
        </div>
        <div class="stepper-bar" role="progressbar" aria-label="Progress" aria-valuemin={0} aria-valuemax={steps.length} aria-valuenow={at + 1}>
          <span style={{ width: `${((at + 1) / steps.length) * 100}%` }} />
        </div>
        {next ? <div class="stepper-next">Next: {next.name}</div> : null}
      </div>
    );
  }

  return (
    <ol class="stepper" aria-label={label}>
      {steps.map((s, i) => {
        const done = i < at;
        const content = (
          <>
            <span class="stepper-num" aria-hidden="true">
              {done ? '✓' : i + 1}
            </span>
            <span>{s.name}</span>
            {done ? <span class="visually-hidden"> (done)</span> : null}
          </>
        );
        return (
          <li key={s.key} class={i === at ? 'on' : done ? 'done' : ''} aria-current={i === at ? 'step' : undefined}>
            {onPick && i !== at && reach(s.key, i) ? (
              <button type="button" class="stepper-step" onClick={() => onPick(s.key)}>
                {content}
              </button>
            ) : (
              <span class="stepper-step">{content}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
